import { unsavedChangesGuard } from './core/unsaved-changes/unsaved-changes.guard';
import { authGuard } from './features/auth/guards/auth.guard';
import { caseContextResolver } from './features/cases/resolvers/case-context.resolver';
import { groupContextResolver } from './features/groups/resolvers/group-context.resolver';
import { projectContextResolver } from './features/projects/resolvers/project-context.resolver';
import { tenantAdminGuard } from './features/tenants/guards/tenant-admin.guard';
import { tenantGuard } from './features/tenants/guards/tenant.guard';
import { tenantContextResolver } from './features/tenants/resolvers/tenant-context.resolver';

import type { Route } from '@angular/router';

export const appRoutes: Route[] = [
  {
    path: 'signin',
    loadComponent: () =>
      import('./pages/signin/signin.page').then((m) => m.SigninPage),
  },
  {
    path: 'tenants',
    loadComponent: () =>
      import('./pages/tenants/tenants.page').then((m) => m.TenantsPage),
    canActivate: [authGuard],
  },
  // 審査系ルート（tenantId 付き）- 階層化構造
  {
    path: 'tenants/:tenantId',
    canActivate: [authGuard, tenantGuard],
    resolve: { tenant: tenantContextResolver },
    children: [
      // グループ一覧（resolver 不要、ページが自分でロード）
      {
        path: 'groups',
        loadComponent: () =>
          import('./pages/groups/groups.page').then((m) => m.GroupsPage),
      },
      // プロジェクト階層（projectContextResolver / groupContextResolver を並列実行）
      {
        path: 'projects/:projectId',
        resolve: {
          project: projectContextResolver,
          group: groupContextResolver,
        },
        children: [
          // プロジェクト詳細（空パス）
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./pages/project-detail/project-detail.page').then(
                (m) => m.ProjectDetailPage,
              ),
            canDeactivate: [unsavedChangesGuard],
          },
          // 証憑ファイル一覧
          {
            path: 'files',
            loadComponent: () =>
              import('./pages/project-files/project-files.page').then(
                (m) => m.ProjectFilesPage,
              ),
          },
          // 証憑プレビュー（座標オーバーレイ付き。別ウィンドウで開かれる想定）
          {
            path: 'evidence-preview',
            loadComponent: () =>
              import('./pages/evidence-preview/evidence-preview.page').then(
                (m) => m.EvidencePreviewPage,
              ),
          },
          // 審査対象階層（caseContextResolver を実行）
          {
            path: 'cases/:caseId',
            resolve: { case: caseContextResolver },
            children: [
              // 雇用審査（空パス）
              {
                path: '',
                pathMatch: 'full',
                loadComponent: () =>
                  import('./pages/employ-case/employ-case.page').then(
                    (m) => m.EmployCasePage,
                  ),
                canDeactivate: [unsavedChangesGuard],
              },
              // 仕訳審査
              {
                path: 'journal-entries/:yearMonth',
                loadComponent: () =>
                  import('./pages/journal-entry-cases/journal-entry-cases.page').then(
                    (m) => m.JournalEntryCasesPage,
                  ),
                canDeactivate: [unsavedChangesGuard],
              },
            ],
          },
        ],
      },
      // 管理系ルート（tenantAdminGuard で保護）
      {
        path: 'users',
        loadComponent: () =>
          import('./pages/users/users.page').then((m) => m.UsersPage),
        canActivate: [tenantAdminGuard],
      },
      {
        path: 'users/:userId',
        loadComponent: () =>
          import('./pages/user-detail/user-detail.page').then(
            (m) => m.UserDetailPage,
          ),
        canActivate: [tenantAdminGuard],
      },
      {
        path: 'policies',
        loadComponent: () =>
          import('./pages/policies/policies.page').then((m) => m.PoliciesPage),
        canActivate: [tenantAdminGuard],
      },
      {
        path: 'policies/:policyId',
        loadComponent: () =>
          import('./pages/policy-detail/policy-detail.page').then(
            (m) => m.PolicyDetailPage,
          ),
        canActivate: [tenantAdminGuard],
      },
    ],
  },
  // 旧審査系 URL からのリダイレクト（ブックマーク・外部リンク対策）
  // tenantId が不明なためテナント選択画面へ誘導する
  {
    path: 'groups',
    redirectTo: '/tenants',
  },
  {
    path: 'projects/:projectId',
    redirectTo: '/tenants',
  },
  {
    path: 'projects/:projectId/files',
    redirectTo: '/tenants',
  },
  {
    path: 'projects/:projectId/cases/:caseId',
    redirectTo: '/tenants',
  },
  {
    path: 'projects/:projectId/cases/:caseId/journal-entries/:yearMonth',
    redirectTo: '/tenants',
  },
  // フォールバック
  {
    path: '',
    redirectTo: 'tenants',
    pathMatch: 'full',
  },
  {
    path: '**',
    redirectTo: 'tenants',
  },
];
